import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// eslint-disable-next-line lodash/import-scope
import type { DebounceSettings } from 'lodash';
import type { ActionCreator, AnyAction } from 'redux';

import { useDebouncedCallback } from '../../../hooks';
import * as PackageInventoryStore from '../../../redux/packageInventory';
import TeamsStore from '../../../redux/teams';

const DEBOUNCE_TIMEOUT = 750;

//  We want requests to happen after the last user action takes place.
//  Read more: https://css-tricks.com/debouncing-throttling-explained-examples/#aa-debounce
const DEBOUNCE_OPTIONS: DebounceSettings = {
  leading: false,
  trailing: true,
};

/**
 * Hook which will return a function which dispatches a given action
 * first, followed by dispatching a debounced version of the async fetchPackageProjects.
  * We have to define it here because we need easy access to the component's params.
  */
const useFetchPackageVersionProjects = (
  actionCreator: ActionCreator<AnyAction>,
  packageLocator: string,
  version: string,
  resetPage: boolean = false,
) => {
  const dispatch = useDispatch();

  //  Memoized debounce function, which re-fetches the packages.
  const dispatchFetchPackages = useDebouncedCallback(
    () => {
      dispatch(PackageInventoryStore.actions.fetchPackageProjects(packageLocator, version));
      dispatch(PackageInventoryStore.actions.fetchPackageVersionMetadata(packageLocator, version));
    },
    DEBOUNCE_TIMEOUT,
    DEBOUNCE_OPTIONS,
  );

  //  This helper does three things, in synchronous order:
  //  - dispatch an action which will update the state immediately in response to user action
  //  - after the debounce timeout, the packages are fetched
  const fetchPackageVersions = useCallback(
    (...args) => {
      if (resetPage) {
        dispatch(PackageInventoryStore.actions.resetVersionDetailsPage());
      }

      //  Dispatch the passed in action, which updates the Redux store immediately
      dispatch(actionCreator(...args));

      dispatchFetchPackages();
    },
    [actionCreator],
  );

  return fetchPackageVersions;
};

/**
 * We also need to check when the teamIds change, but they are stored in the teams slice of the store.
 * This hook essentially "spies" on teams.teamSelection, and will re-fetch the packages when it changes.
 */
export const useRefetchVersionProjectsDetailsOnTeamSelection = ({
  packageLocator,
  version,
}: {
  packageLocator: string,
  version: string,
}) => {
  const dispatch = useDispatch();
  const teamSelection = useSelector(TeamsStore.selectors.getTeamSelection);

  useEffect(() => {
    dispatch(PackageInventoryStore.actions.fetchPackageProjects(packageLocator, version));
    dispatch(PackageInventoryStore.actions.fetchPackageVersionMetadata(packageLocator, version));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- We are intentionally omitting packageLocator, version here to avoid over-fetching/fetching on mount.
  }, [dispatch, teamSelection]);
};

export default useFetchPackageVersionProjects;
